import autolinkText from '@/lib/autolinkText'
import { apiEndpoints } from '@/lib/api/endpoints'
import { syncCareContext } from '@/lib/careContextSync'
import { baseApi } from '@/lib/store/api/baseQuery'
import type { CareType } from '@/types/interfaces/care'

export interface CarePlanNextStep {
    id: string
    type: string
    state: string
    lab_names?: string[]
    scheduled_at?: string | null
    lab_order_id?: string | null
    details?: {
        labs?: Array<{ id: string; name: string }>
        questionnaire_id?: string
    }
    patient_rationale?: string
    questionnaire_name?: string
    questionnaire_url?: string
    questionnaire_response_id?: string | null
}

const formatCare = (care: CareType): CareType => ({
    ...care,
    provider_message: autolinkText(care.provider_message ?? null),
    patient_instructions: autolinkText(care.patient_instructions ?? null),
})

const persistSessionCareId = (careId: string) => {
    if (typeof window === 'undefined') return
    try {
        window.sessionStorage.setItem('careId', JSON.stringify(careId))
    } catch {
        // sessionStorage may be unavailable in private browsing or quota errors
    }
}

const transientHttpStatus = (error: unknown): number | undefined => {
    if (!error || typeof error !== 'object') return undefined
    const record = error as { status?: unknown; originalStatus?: unknown }
    if (typeof record.status === 'number') return record.status
    if (typeof record.originalStatus === 'number') return record.originalStatus
    return undefined
}

const retryTransientRequest = (error: unknown, _args: unknown, { attempt }: { attempt: number }): boolean => {
    if (attempt > 2) return false
    const status = typeof error === 'object' && error !== null && 'status' in error ? error.status : undefined
    if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true
    const httpStatus = transientHttpStatus(error)
    return typeof httpStatus === 'number' && httpStatus >= 500
}

export const carePlanApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getCarePlan: builder.query<CareType, string>({
            query: (careId) => apiEndpoints.care.getById(careId),
            transformResponse: (response: CareType) => formatCare(response),
            providesTags: (_result, _error, careId) => [{ type: 'CarePlan', id: careId }],
            extraOptions: { retryCondition: retryTransientRequest },
            async onQueryStarted(careId, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    syncCareContext(data)
                    persistSessionCareId(careId)
                } catch {
                    // Leave CareContext unchanged on failure; the hook surfaces emptyCare.
                }
            },
        }),
        getCarePlanNextSteps: builder.query<CarePlanNextStep[], string>({
            query: (careId) => apiEndpoints.care.getNextSteps(careId),
            providesTags: (_result, _error, careId) => [{ type: 'CarePlan', id: careId }],
            extraOptions: { retryCondition: retryTransientRequest },
        }),
    }),
    overrideExisting: false,
})

export const { useGetCarePlanQuery, useGetCarePlanNextStepsQuery } = carePlanApi
